import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getClubTeams } from 'api/club/teamApi';
import { clubFeaturesOf, clubForId } from 'utils/clubFeatures';
import { teamWordsOf } from 'utils/teamLabel';

// The teams of the club on screen (school houses, factions), for a page that
// shows or sets an athlete's team.
//
// The club is the one passed in, and otherwise the selected club. Whether it
// has teams, and its word for them, are read from the club list already in
// memory. The team list itself is one request, made only for a club that has
// teams; the server answers it from its cache. A club without teams makes no
// request and gets an empty list, so a page can use this without asking
// first.
//
//   hasTeams    whether the club has teams at all
//   teams       [{ teamID, name, colour, sortOrder }], in the club's order
//   teamWords   the club's words for a team (utils/teamLabel.js)
//   reloadTeams fetches the list again
const useClubTeams = (clubId) => {
  const club = useSelector((state) => clubForId(state.user, clubId));
  const hasTeams = clubFeaturesOf(club).teams;
  const teamClubId = club?.clubID || '';
  const [teams, setTeams] = useState([]);

  const reloadTeams = useCallback(async () => {
    if (!hasTeams || !teamClubId) {
      setTeams([]);
      return;
    }
    try {
      const response = await getClubTeams(teamClubId);
      setTeams(response.data || []);
    } catch (err) {
      // The page still works without the list: teams show as blank.
      console.log('[useClubTeams] Fetch teams error: ', err);
      setTeams([]);
    }
  }, [hasTeams, teamClubId]);

  useEffect(() => {
    reloadTeams();
  }, [reloadTeams]);

  return { hasTeams, teams, teamWords: teamWordsOf(club), reloadTeams };
};

export default useClubTeams;
